import { useEffect, useRef, useState } from 'react'

/**
 * Milliseconds remaining until `targetLocalMs`, updated every animation frame.
 *
 * A game clock driven by `setInterval` visibly stutters, and a 5-second answer
 * window is short enough that the stutter reads as unfairness. rAF also pauses
 * with the tab, which is the behaviour we want.
 */
export function useCountdown(targetLocalMs: number | null): number {
  const [remaining, setRemaining] = useState(() =>
    targetLocalMs === null ? 0 : Math.max(0, targetLocalMs - Date.now()),
  )
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (targetLocalMs === null) {
      setRemaining(0)
      return
    }

    const tick = () => {
      const left = Math.max(0, targetLocalMs - Date.now())
      setRemaining(left)
      if (left > 0) frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [targetLocalMs])

  return remaining
}

/**
 * Milliseconds elapsed since `startLocalMs`, capped at `durationMs`.
 * Used to drive the counting-mode stages, which need "where am I in this
 * round" rather than "how long is left".
 */
export function useElapsed(startLocalMs: number | null, durationMs: number): number {
  const [elapsed, setElapsed] = useState(0)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (startLocalMs === null) {
      setElapsed(0)
      return
    }

    const tick = () => {
      const value = Date.now() - startLocalMs
      setElapsed(Math.min(value, durationMs))
      if (value < durationMs) frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [startLocalMs, durationMs])

  return elapsed
}
